import { ReactElement, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { Mutation_Root, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { resolveApplicationAssessmentDetailsRoot } from 'src/routing/routing-utils'
import {
  queryAssessmentRecommendationById,
  updateAssessmentRecommendationById,
} from 'src/screens/shared/application/assessment/details/recommendation/assessmentRecomendationQueries'
import {
  ApplicationAssessmentRecommendationEditForm,
  AssessmentRecommendationFormValues,
} from 'src/screens/shared/application/assessment/details/recommendation/edit/ApplicationAssessmentRecommendationEditForm'
import { usePermissionsForProjectAssessment } from 'src/service/security/PermissionHook'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { Utils } from 'src/shared/utils/Utils'
import { useClient } from 'urql'

type originType = 'SAVE' | 'SAVE_AND_BACK'

interface Props {
  baseUrl: '/pf-kap' | '/pf-pgv'
}

export const ApplicationAssessmentRecommendationEditPage = ({ baseUrl }: Props): ReactElement => {
  const [initialValues, setInitialValues] = useState<AssessmentRecommendationFormValues>()
  const { getMessage } = useMessageSource()
  const projectType = Utils.resolveProcess(baseUrl)
  const process = Utils.resolveProcessToLowerCase(baseUrl)
  const { projectId, assessmentId } = useParams()
  const navigate = useDelayedNavigate()
  const urqlClient = useClient()
  const originRef = useRef<originType>('SAVE')
  const notificationService = useNotificationService()

  const { loading, canEdit } = usePermissionsForProjectAssessment(
    projectType,
    parseInt(projectId as string),
    parseInt(assessmentId as string),
  )

  useEffect(() => {
    const initFormValues = async () => {
      const { data } = await urqlClient
        .query<{ project_assessment: Query_Root['project_assessment'] }>(queryAssessmentRecommendationById, {
          assessmentId,
        })
        .toPromise()

      if (data && data.project_assessment[0]) {
        const assessment = data.project_assessment[0]
        setInitialValues({
          recommendation: assessment?.recommendation || null,
          pros: assessment?.pros || null,
          cons: assessment?.cons || null,
          comment: assessment?.recommendation_comment || null,
          version: assessment?.version || 0,
        })
      }
    }

    initFormValues()
  }, [assessmentId, urqlClient])

  const onSubmit = (origin: originType) => (_: any) => {
    // trigger submit event
    if (originRef) {
      originRef.current = origin
    }
    document.getElementById('edit-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  const updateHandler = async (values: AssessmentRecommendationFormValues) => {
    const updateObject = {
      assessmentId: assessmentId,
      recommendation: values.recommendation,
      pros: values.pros,
      cons: values.cons,
      comment: values.comment,
      version: values.version,
    }

    const { error, data } = await urqlClient
      .mutation<{ update_project_assessment: Mutation_Root['update_project_assessment'] }>(
        updateAssessmentRecommendationById,
        updateObject,
      )
      .toPromise()

    if (error || data?.update_project_assessment?.affected_rows !== 1) {
      notificationService.operationFailed()
    } else {
      const newVersion = data!.update_project_assessment.returning[0].version
      const newInitialValues = { ...values, version: newVersion } as AssessmentRecommendationFormValues
      setInitialValues(newInitialValues)
      notificationService.changesSaved()
      if (originRef.current === 'SAVE_AND_BACK') {
        navigate(
          resolveApplicationAssessmentDetailsRoot(baseUrl).nested.Recommendation.params({
            projectId,
            assessmentId,
          }),
        )
      }
    }
  }

  if (!loading && !canEdit) {
    return <NotAuthorized />
  }

  return (
    <ScreenLayout
      title={getMessage('label.assessment.recommendation.edit')}
      hasSecondLevelNavigation={false}
      onBack={() => {
        navigate(
          resolveApplicationAssessmentDetailsRoot(baseUrl).nested.Recommendation.params({ projectId, assessmentId }),
        )
      }}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSubmit('SAVE_AND_BACK')} />
          <SaveButton origin="header" onClick={onSubmit('SAVE')} />
        </>
      }
    >
      <PageLayout>
        <>
          {!loading && initialValues && (
            <>
              <HelpAndInstructions labelKey={`label.help.assessment.recommendation.${process}`} defaultExpansion />
              <ApplicationAssessmentRecommendationEditForm initialValues={initialValues} onSave={updateHandler} />
            </>
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
